import { z } from 'zod';
import { ServiceCategoryDto } from './service-category';

export const ServiceFormatEnum = z.enum(['AT_PROVIDER', 'AT_CLIENT', 'ONLINE']);
export type ServiceFormat = z.infer<typeof ServiceFormatEnum>;

export const ServiceStatusEnum = z.enum(['DRAFT', 'PENDING_MODERATION', 'PUBLISHED', 'ARCHIVED']);
export type ServiceStatus = z.infer<typeof ServiceStatusEnum>;

const serviceCreateBase = z.object({
  title: z.string().trim().min(1).max(120),
  description: z.string().max(4000).nullable().optional(),
  priceText: z.string().trim().max(60).nullable().optional(),
  format: ServiceFormatEnum.default('AT_PROVIDER'),
  addressText: z.string().trim().max(200).nullable().optional(),
  categoryIds: z.array(z.uuid()).length(1),
});

export const CreateServiceInput = serviceCreateBase;
export type CreateServiceInput = z.infer<typeof CreateServiceInput>;

export const UpdateServiceInput = serviceCreateBase.partial().extend({
  status: z.enum(['ARCHIVED']).optional(),
});
export type UpdateServiceInput = z.infer<typeof UpdateServiceInput>;

export const ServiceListQuery = z.object({
  cityId: z.uuid().optional(),
  categoryId: z.uuid().optional(),
  /**
   * Тема запроса: ищется в названии И в описании (публичный каталог). Админский
   * список (`ServiceAdminListQuery.q`) — свой параметр со своим `where` и ищет
   * по-прежнему только по названию: там модерируют конкретную карточку, а не
   * подбирают услугу по смыслу.
   */
  search: z.string().trim().max(200).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ServiceListQuery = z.infer<typeof ServiceListQuery>;

export const ServiceAdminListQuery = z.object({
  cityId: z.uuid().optional(),
  categoryId: z.uuid().optional(),
  status: ServiceStatusEnum.optional(),
  q: z.string().trim().max(120).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ServiceAdminListQuery = z.infer<typeof ServiceAdminListQuery>;

export const ServiceProviderDto = z.object({
  id: z.uuid(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  photoUrl: z.string().nullable(),
});
export type ServiceProviderDto = z.infer<typeof ServiceProviderDto>;

export const ServiceDto = z.object({
  id: z.uuid(),
  cityId: z.uuid(),
  provider: ServiceProviderDto.nullable(),
  providerUsername: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  priceText: z.string().nullable(),
  format: ServiceFormatEnum,
  addressText: z.string().nullable(),
  coverUrl: z.string().nullable(),
  status: ServiceStatusEnum,
  rejectionReason: z.string().nullable(),
  categories: z.array(ServiceCategoryDto),
  isMine: z.boolean(),
  likeCount: z.number().int(),
  isLikedByMe: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ServiceDto = z.infer<typeof ServiceDto>;

export const ServiceListItemDto = ServiceDto.omit({ description: true });
export type ServiceListItemDto = z.infer<typeof ServiceListItemDto>;

export const ServiceListResponse = z.object({
  items: z.array(ServiceListItemDto),
  nextCursor: z.string().nullable(),
});
export type ServiceListResponse = z.infer<typeof ServiceListResponse>;

export const RejectServiceInput = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type RejectServiceInput = z.infer<typeof RejectServiceInput>;

export const NhatrangGuideImportResult = z.object({
  created: z.number().int(),
  skipped: z.number().int(),
  createdTitles: z.array(z.string()),
});
export type NhatrangGuideImportResult = z.infer<typeof NhatrangGuideImportResult>;
